import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import {
  ClockModeAreaConfig,
  ClockModeConfig,
  ClockModeComponentType,
  updateClockModeAreaAtom,
  TaskTimeRange,
} from "../../../../atoms/v3/clockModeAtoms";
import { tasksAtom, fetchTasksAtom } from "../../../../atoms/v0/taskAtoms";
import { EmptyArea } from "./components/EmptyArea";
import { CustomTextArea } from "./components/CustomTextArea";
import { TaskCountdownArea } from "./components/TaskCountdownArea";
import { TaskTimerArea, TaskTimerAreaState } from "./components/TaskTimerArea";
import { RecentTasksArea } from "./components/RecentTasksArea";
import { TodayEfficiencyArea } from "./components/TodayEfficiencyArea";
import { WeeklyEfficiencyArea } from "./components/WeeklyEfficiencyArea";
import ClockModeIcon from "./ClockModeIcon";
import ClockSettingsModal from "./components/ClockSettingsModal";
import ClearConfigurableAreaButton from "./components/ClearConfigurableAreaButton";
import { useNavigate } from "react-router-dom";
import {
  WheelPicker,
  WheelPickerWrapper,
  type WheelPickerOption,
} from "@ncdai/react-wheel-picker";
import { TaskItemShared } from "../tasks/base";
import { playTimerSoundOnce, startTestSound, stopTestSound, isTestingSoundActive, testVibration } from "../../../../services/timerNotification";

interface ConfigurableAreaProps {
  areaId: keyof ClockModeConfig;
  config: ClockModeAreaConfig;
  currentTime: Date;
  isConfigMode?: boolean;
}

// const timerDurationList = [10, 20, 45, 60];
const createTimeSelectorArray = (start: number, end: number, step: number) => {
  const result: WheelPickerOption[] = [];
  for (let i = start; i <= end; i += step) {
    result.push({
      label: i.toString(),
      value: i.toString(),
    });
  }
  return result;
};

const minutesArray = createTimeSelectorArray(0, 120, 1); // 0 到 120 分钟
const secondsArray = createTimeSelectorArray(0, 59, 1); // 1 到 59 秒

/**
 * 可配置区域组件
 * 根据配置类型渲染不同的组件，支持快速切换组件类型
 */
export const ConfigurableArea: React.FC<ConfigurableAreaProps> = ({
  areaId,
  config,
  currentTime,
  isConfigMode = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["v3/clock"]);
  const [, updateArea] = useAtom(updateClockModeAreaAtom);
  const tasks = useAtomValue(tasksAtom);
  const fetchTasks = useSetAtom(fetchTasksAtom);
  const [showComponentSelector, setShowComponentSelector] = useState(false);
  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [showCountdownBaseSelector, setShowCountdownBaseSelector] =
    useState(false);
  const [showTimerDurationSelector, setShowTimerDurationSelector] =
    useState(false);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showTaskListEditor, setShowTaskListEditor] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [customTextDraft, setCustomTextDraft] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [showTimerEditor, setShowTimerEditor] = useState(false);
  const [useTaskDateFilter, setUseTaskDateFilter] = useState(true);
  const [isTestingSound, setIsTestingSound] = useState(false);

  const linkedTask = tasks.find((task) => task.id === config.taskId);
  const formatDate = TaskItemShared.createFormatDate(t);

  // 确保任务数据已加载
  useEffect(() => {
    if (tasks.length === 0) {
      fetchTasks();
    }
  }, [tasks.length, fetchTasks]);

  // 自动选中 textarea
  useEffect(() => {
    if (showTextEditor && textAreaRef.current) {
      // textAreaRef.current.focus();
      // textAreaRef.current.select();
      // 在 iOS 上自动选择目前仍有 bug，所以暂时不使用自动选择
    }
  }, [showTextEditor]);

  // 组件类型选项
  const componentTypes: Array<{
    value: ClockModeComponentType;
    label: string;
    icon: string;
  }> = [
    // {
    //   value: "empty",
    //   label: t("v3/clock:settings.components.empty", "空白"),
    //   icon: "⚙",
    // },
    {
      value: "custom-text",
      label: t("v3/clock:settings.components.customText", "文字记录"),
      icon: "📝",
    },
    {
      value: "task-countdown",
      label: t("v3/clock:settings.components.taskCountdown", "倒数日"),
      icon: "⏰",
    },
    {
      value: "task-timer",
      label: t("v3/clock:settings.components.taskTimer", "计时器"),
      icon: "⏱️",
    },
    {
      value: "recent-tasks",
      label: t("v3/clock:settings.components.recentTasks", "待办事项"),
      icon: "📋",
    },
    // {
    //   value: "today-efficiency",
    //   label: t("v3/clock:settings.components.todayEfficiency", "今日效率"),
    //   icon: "📊",
    // },
    // {
    //   value: "weekly-efficiency",
    //   label: t("v3/clock:settings.components.weeklyEfficiency", "本周效率"),
    //   icon: "📈",
    // },
  ];

  // 切换组件类型
  const handleComponentTypeChange = (newType: ClockModeComponentType) => {
    updateArea({
      areaId,
      config: {
        type: newType,
        // 保留相关配置
        ...(newType === "custom-text"
          ? { customText: config.customText || "" }
          : {}),
        ...(newType === "task-countdown"
          ? {
              taskId: config.taskId,
              countdownBase: config.countdownBase || "due",
            }
          : {}),
        ...(newType === "task-timer"
          ? {
              taskId: config.taskId,
              timerDuration: config.timerDuration !== undefined ? config.timerDuration : 45,
              timerDurationSeconds: config.timerDurationSeconds || 0,
              soundNotification: config.soundNotification || false,
              vibrationNotification: config.vibrationNotification || false,
              soundFile: config.soundFile || 'vivora',
            }
          : {}),
      },
    });
    setShowComponentSelector(false);

    // 根据 type 显示相应的配置面板
    switch (newType) {
      case "custom-text":
        setShowTextEditor(true);
        break;
      case "task-countdown":
      case "task-timer":
        setShowTimerEditor(true);
        break;
      default:
        break;
    }
  };

  const handleClearArea = () => {
    handleComponentTypeChange("empty");

    setIsEditing(false);
    setShowComponentSelector(false);
    setShowTextEditor(false);
    // setShowTaskSelector(false);
    setShowTaskListEditor(false);
    setShowTimerEditor(false);
  };

  // 处理时间范围选择
  const handleTimeRangeSelect = (timeRange: TaskTimeRange) => {
    updateArea({
      areaId,
      config: {
        ...config,
        taskTimeRange: timeRange,
      },
    });
  };

  // 选择任务
  const handleTaskSelect = (taskId: string) => {
    const configData = {
      ...config,
      taskId,
    };

    if (config.type === "task-countdown") {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        if (
          (config.countdownBase === "start" && !task.startAt) ||
          (config.countdownBase === "due" && !task.dueAt) ||
          (config.countdownBase === "end" && !task.endAt)
        ) {
          configData.countdownBase = task.dueAt
            ? "due"
            : task.startAt
              ? "start"
              : task.endAt
                ? "end"
                : "due";
        }
      }
    }

    updateArea({
      areaId,
      config: configData,
    });
  };

  // 选择倒数基准时间
  const handleCountdownBaseSelect = (
    countdownBase: "start" | "due" | "end"
  ) => {
    updateArea({
      areaId,
      config: {
        ...config,
        countdownBase,
      },
    });
    setShowCountdownBaseSelector(false);
  };

  // 选择计时器时长
  const handleTimerDurationSelect = (
    timerDuration: number,
    timerDurationSeconds: number
  ) => {
    updateArea({
      areaId,
      config: {
        ...config,
        timerDuration,
        timerDurationSeconds,
      },
    });
    setShowTimerDurationSelector(false);
  };

  // 处理铃声提醒开关
  const handleSoundNotificationToggle = (enabled: boolean) => {
    // 如果关闭铃声提醒且正在试听，停止试听
    if (!enabled && isTestingSound) {
      stopTestSound();
      setIsTestingSound(false);
    }

    updateArea({
      areaId,
      config: {
        ...config,
        soundNotification: enabled,
      },
    });
  };

  // 处理震动提醒开关
  const handleVibrationNotificationToggle = (enabled: boolean) => {
    updateArea({
      areaId,
      config: {
        ...config,
        vibrationNotification: enabled,
      },
    });
  };

  // 处理音频文件选择
  const handleSoundFileChange = (soundFile: string) => {
    // 如果正在试听，先停止当前试听
    if (isTestingSound) {
      stopTestSound();
      setIsTestingSound(false);
    }

    updateArea({
      areaId,
      config: {
        ...config,
        soundFile: soundFile as 'vivora' | 'delay' | 'sms',
      },
    });
  };

  // 处理试听铃声
  const handleTestSound = async () => {
    if (isTestingSound) {
      // 停止试听
      stopTestSound();
      setIsTestingSound(false);
    } else {
      // 开始试听
      setIsTestingSound(true);
      try {
        await startTestSound(config.soundFile || 'vivora');
        // 确认试听状态
        setIsTestingSound(isTestingSoundActive());
      } catch (error) {
        console.error('Failed to start test sound:', error);
        setIsTestingSound(false);
      }
    }
  };

  // 停止试听音频的辅助函数
  const stopTestAudio = () => {
    stopTestSound();
    setIsTestingSound(false);
  };

  // 处理计时器编辑器关闭
  const handleTimerEditorClose = () => {
    // 关闭编辑器时停止试听音频
    if (isTestingSound) {
      stopTestSound();
      setIsTestingSound(false);
    }
  };

  // 处理测试震动
  const handleTestVibration = async () => {
    try {
      await testVibration();
    } catch (error) {
      console.error('Failed to test vibration:', error);
    }
  };

  // 组件卸载时清理音频
  useEffect(() => {
    return () => {
      stopTestSound();
      setIsTestingSound(false);
    };
  }, []);

  // 编辑自定义文字
  const handleEditCustomText = () => {
    setCustomTextDraft(config.customText || ""); // 初始化
    setIsEditing(true);
    setShowTextEditor(true);
  };

  const handleTextChange = (newText: string) => {
    updateArea({
      areaId,
      config: {
        ...config,
        customText: newText,
      },
    });
    setIsEditing(false);
    setShowTextEditor(false);
  };

  // 关闭编辑器时应用文本
  const handleTextEditorClose = (show: boolean) => {
    setShowTextEditor(show);
    setIsEditing(false);
    if (!show) {
      // 关闭时应用
      handleTextChange(customTextDraft);
    }
  };

  // 更新按钮位置
  const updateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  };

  const handleTimerSaveState = (state: TaskTimerAreaState) => {
    updateArea({
      areaId,
      config: {
        ...config,
        savedState: state,
      },
    });
  };

  // 计算菜单位置，避让屏幕边界
  const calculateMenuPosition = (
    menuHeight: number = 200,
    menuWidth: number = 200
  ) => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = buttonPosition.top;
    let left = buttonPosition.left;

    // 检查下边界溢出
    if (top + menuHeight > viewportHeight) {
      // 如果菜单会溢出下边界，则显示在按钮上方
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        top = rect.top - menuHeight - 8;
      }

      // 如果上方也不够空间，则贴着屏幕底部显示
      if (top < 0) {
        top = viewportHeight - menuHeight - 16;
      }
    }

    // 检查右边界溢出
    if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 16;
    }

    // 检查左边界溢出
    if (left < 16) {
      left = 16;
    }

    return { top, left };
  };

  // 显示组件选择器
  const handleShowComponentSelector = () => {
    updateButtonPosition();
    setShowComponentSelector(!showComponentSelector);
    // 关闭其他菜单
    setShowTaskSelector(false);
    setShowCountdownBaseSelector(false);
    setShowTimerDurationSelector(false);
    setShowTaskListEditor(false);
  };

  // // 显示任务选择器
  // const handleShowTaskSelector = () => {
  //   updateButtonPosition();
  //   setShowTaskSelector(!showTaskSelector);
  //   // 关闭其他菜单
  //   setShowComponentSelector(false);
  //   setShowCountdownBaseSelector(false);
  //   setShowTimerDurationSelector(false);
  //   setShowTaskListEditor(false);
  // };

  // // 显示倒数基准选择器
  // const handleShowCountdownBaseSelector = () => {
  //   updateButtonPosition();
  //   setShowCountdownBaseSelector(!showCountdownBaseSelector);
  //   // 关闭其他菜单
  //   setShowComponentSelector(false);
  //   setShowTaskSelector(false);
  //   setShowTimerDurationSelector(false);
  //   setShowCountdownBaseSelector(false);
  // };

  // // 显示计时器时长选择器
  // const handleShowTimerDurationSelector = () => {
  //   updateButtonPosition();
  //   setShowTimerDurationSelector(!showTimerDurationSelector);
  //   // 关闭其他菜单
  //   setShowComponentSelector(false);
  //   setShowTaskSelector(false);
  //   setShowCountdownBaseSelector(false);
  //   setShowTimerDurationSelector(false);
  // };

  // 显示任务列表编辑器
  const handleEditTasksArea = () => {
    // updateButtonPosition();
    setShowTaskListEditor(!showTaskListEditor);
    // 关闭其他菜单
    setShowComponentSelector(false);
    setShowTimerEditor(false);
    setShowTextEditor(false);
  };

  const handleEditTimerArea = () => {
    setShowTimerEditor(true);
  };

  // 渲染对应的组件
  const renderComponent = () => {
    switch (config.type) {
      case "empty":
        return <EmptyArea onClick={() => handleShowComponentSelector()} />;

      case "custom-text":
        return (
          <CustomTextArea
            text={config.customText || ""}
            onEdit={handleEditCustomText}
          />
        );

      case "task-countdown":
        return (
          <TaskCountdownArea
            taskId={config.taskId}
            currentTime={currentTime}
            countdownBase={config.countdownBase || "due"}
            onEditArea={handleEditTimerArea}
          />
        );

      case "task-timer":
        return (
          <TaskTimerArea
            taskId={config.taskId}
            currentTime={currentTime}
            timerDuration={config.timerDuration !== undefined ? config.timerDuration : 45}
            timerDurationSeconds={config.timerDurationSeconds || 0}
            onEditArea={handleEditTimerArea}
            onSaveState={handleTimerSaveState}
            initialState={config.savedState as TaskTimerAreaState}
            soundNotification={config.soundNotification || false}
            vibrationNotification={config.vibrationNotification || false}
            soundFile={config.soundFile || 'vivora'}
          />
        );

      case "recent-tasks":
        return (
          <RecentTasksArea
            currentTime={currentTime}
            onEditArea={handleEditTasksArea}
            timeRange={config.taskTimeRange}
          />
        );

      case "today-efficiency":
        return <TodayEfficiencyArea currentTime={currentTime} />;

      case "weekly-efficiency":
        return <WeeklyEfficiencyArea currentTime={currentTime} />;

      default:
        return <EmptyArea />;
    }
  };

  // 获取当前组件类型的图标
  // const getCurrentIcon = () => {
  //   return (
  //     componentTypes.find((type) => type.value === config.type)?.icon || "⚙"
  //   );
  // };

  return (
    <div
      className="h-full w-full relative group"
      // onMouseEnter={() => setIsHovered(true)}
      // onMouseLeave={() => setIsHovered(false)}
    >
      {/* 主要内容区域 */}
      <div className="h-full w-full clock-mode-area-transition">
        {renderComponent()}
      </div>

      {/* 配置按钮 - 悬停时显示 */}
      {(isEditing ||
        showComponentSelector ||
        showTaskSelector ||
        showCountdownBaseSelector ||
        showTimerDurationSelector) &&
        // <div className="absolute top-10 right-4 z-10 flex flex-row-reverse space-x-reverse space-x-1">
        //   {/* 组件类型按钮 - 在最右边，有特殊描边 */}
        //   <button
        //     ref={buttonRef}
        //     onClick={handleShowComponentSelector}
        //     className="p-1.5 rounded-lg bg-black/20 text-white/80 hover:bg-black/30 hover:text-white transition-all duration-200 backdrop-blur-sm text-xs border-2 border-blue/30 hover:border-blue/50"
        //     title={t("v3/clock:settings.componentType", "组件类型")}
        //   >
        //     {getCurrentIcon()}
        //   </button>

        //   {/* 自定义文字编辑按钮 */}
        //   {config.type === "custom-text" && (
        //     <button
        //       onClick={handleEditCustomText}
        //       className="p-1.5 rounded-lg bg-black/20 text-white/80 hover:bg-black/30 hover:text-white transition-all duration-200 backdrop-blur-sm text-xs"
        //       title={t("v3/clock:controls.editText", "编辑文字")}
        //     >
        //       ✏️
        //     </button>
        //   )}

        //   {/* 任务选择按钮 - 仅在任务相关组件时显示 */}
        //   {(config.type === "task-countdown" ||
        //     config.type === "task-timer") && (
        //     <button
        //       onClick={handleShowTaskSelector}
        //       className="p-1.5 rounded-lg bg-black/20 text-white/80 hover:bg-black/30 hover:text-white transition-all duration-200 backdrop-blur-sm text-xs"
        //       title={t("v3/clock:settings.selectTask", "选择任务")}
        //     >
        //       📋
        //     </button>
        //   )}

        //   {/* 倒数基准时间选择按钮 - 仅在任务倒数日组件时显示 */}
        //   {config.type === "task-countdown" && (
        //     <button
        //       onClick={handleShowCountdownBaseSelector}
        //       className="p-1.5 rounded-lg bg-black/20 text-white/80 hover:bg-black/30 hover:text-white transition-all duration-200 backdrop-blur-sm text-xs"
        //       title={t("v3/clock:controls.countdownBase", "倒数目标")}
        //     >
        //       🎯
        //     </button>
        //   )}

        //   {/* 计时器时长选择按钮 - 仅在任务计时器组件时显示 */}
        //   {config.type === "task-timer" && (
        //     <button
        //       onClick={handleShowTimerDurationSelector}
        //       className="p-1.5 rounded-lg bg-black/20 text-white/80 hover:bg-black/30 hover:text-white transition-all duration-200 backdrop-blur-sm text-xs"
        //       title={t("v3/clock:controls.timerDuration", "计时时长")}
        //     >
        //       ⏱️
        //     </button>
        //   )}
        // </div>
        null}

      {/* 组件选择器 - 使用 Portal */}
      {showComponentSelector && (
        <ClockSettingsModal
          show={showComponentSelector}
          setShow={setShowComponentSelector}
          header={"选择小组件"}
        >
          <div className="inner">
            {/* <span className="tips">
              {"选择你希望放置在这里的小组件。"}
            </span>
            <div className="spacer"></div> */}
            <span className="settings-header">
              {"选择你希望放置在这里的小组件。"}
            </span>
            <div className="grid grid-cols-2 gap-2">
              {componentTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleComponentTypeChange(type.value)}
                  className={`flex items-center action-button type-select-button ${
                    ""
                    // config.type === type.value
                    //   ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                    //   : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <span className="emoji-icon">{type.icon}</span>
                  <span className="type-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        </ClockSettingsModal>
      )}

      {showTimerEditor && (
        <ClockSettingsModal
          show={showTimerEditor}
          setShow={setShowTimerEditor}
          header={`编辑「${config.type === "task-countdown" ? "倒数日" : "计时器"}」小组件`}
          onClose={handleTimerEditorClose}
        >
          <div className="inner">
            <span className="tips">{`${config.type === "task-countdown" ? "选择关于某个任务的特定时间节点，然后查看距离那时的天数。" : "设定倒计时来进行专注。可以关联任务。"}`}</span>
            <div className="spacer"></div>

            {config.type === "task-timer" && (
              <>
                <span className="settings-header">{"计时长度"}</span>
                <div className="option-list relative">
                  {/* {timerDurationList.map((duration) => (
                    <button
                      key={duration}
                      onClick={() =>
                        handleTimerDurationSelect(duration)
                      }
                      className={`option-button ${
                        config.timerDuration === duration
                          ? "active"
                          : ""
                      }`}
                    >
                      <span className="text-sm">⏱️</span>
                      <span>
                        {duration} {t("v3/clock:timer.minutes", "分钟")}
                      </span>
                    </button>
                  ))} */}
                  <div style={{ touchAction: 'pan-y' }}>
                    <WheelPickerWrapper>
                    <WheelPicker
                      options={minutesArray}
                      value={
                        (config.timerDuration !== undefined && config.timerDuration >= 0 && config.timerDuration <= 120)
                          ? Math.floor(config.timerDuration).toString()
                          : "45"
                      }
                      onValueChange={(value) => {
                        const minutes = parseInt(value, 10);
                        handleTimerDurationSelect(
                          minutes,
                          config.timerDurationSeconds || 0
                        );
                      }}
                      visibleCount={12}
                      optionItemHeight={32}
                    />
                    <WheelPicker
                      options={secondsArray}
                      value={config.timerDurationSeconds?.toString() || "0"}
                      onValueChange={(value) => {
                        handleTimerDurationSelect(
                          config.timerDuration !== undefined ? config.timerDuration : 45,
                          parseInt(value, 10)
                        );
                      }}
                      visibleCount={12}
                      optionItemHeight={32}
                    />
                  </WheelPickerWrapper>
                    <div className="absolute left-0 top-0 w-full h-full pointer-events-none flex" style={{ touchAction: 'none' }}>
                      <div className="grow flex items-center justify-center w-1 leading-none">
                        <span className="block text-[12px] pl-[60px]">
                          {"分钟"}
                        </span>
                      </div>
                      <div className="grow flex items-center justify-center w-1 leading-none">
                        <span className="block text-[12px] pl-[38px]">
                          {"秒"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="spacer"></div>
              </>
            )}

            <span className="settings-header">
              {"关联任务"}
              {config.type === "task-countdown" && (
                <>
                  <div className="grow w-1"></div>
                  <button
                    className="text-blue-500 flex items-center gap-[4px] hover:opacity-80 active:opacity-50 transition duration-200"
                    onClick={() => {
                      setUseTaskDateFilter(!useTaskDateFilter);
                    }}
                  >
                    <ClockModeIcon type={useTaskDateFilter ? "filter-on" : "filter-off"} size={16} />
                    {useTaskDateFilter ? "仅显示有效任务" : "显示全部任务"}
                  </button>
                </>
              )}
            </span>
            <div className="option-list">
              <button
                onClick={() => handleTaskSelect("")}
                className={`option-button ${!config.taskId ? "active" : ""}`}
              >
                <span className="text-sm">⚙</span>
                <span>
                  {config.type === "task-countdown"
                    ? "未选择任务"
                    : "不关联任务"}
                </span>
              </button>
              {tasks
                .filter(
                  (task) =>
                    !task.done &&
                    (config.type !== "task-countdown" ||
                      task.startAt ||
                      task.dueAt ||
                      task.endAt ||
                      !useTaskDateFilter)
                )
                .map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleTaskSelect(task.id)}
                    className={`option-button ${
                      config.taskId === task.id ? "active" : ""
                    }`}
                  >
                    <span className="text-sm">📋</span>
                    <span className="truncate">{task.title}</span>
                  </button>
                ))}
              {tasks.filter((task) => !task.done).length === 0 && (
                <div className="center-tips">
                  {t("v3/clock:settings.noTasksAvailable", "暂无可用任务")}
                </div>
              )}
            </div>

            {config.type === "task-countdown" && (
              <>
                <div className="spacer"></div>
                <span className="settings-header">{"倒数时间"}</span>
                <div className="option-list">
                  {[
                    {
                      value: "start",
                      label: t("v3/clock:controls.countdownStart", "开始时间"),
                      icon: "🚀",
                    },
                    {
                      value: "due",
                      label: t("v3/clock:controls.countdownDue", "截止时间"),
                      icon: "⏰",
                    },
                    {
                      value: "end",
                      label: t("v3/clock:controls.countdownEnd", "结束时间"),
                      icon: "🏁",
                    },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        handleCountdownBaseSelect(
                          option.value as "start" | "due" | "end"
                        )
                      }
                      className={`option-button ${
                        config.countdownBase === option.value ? "active" : ""
                      }`}
                      disabled={
                        !linkedTask ||
                        (option.value === "start" && !linkedTask.startAt) ||
                        (option.value === "due" && !linkedTask.dueAt) ||
                        (option.value === "end" && !linkedTask.endAt)
                      }
                    >
                      <span className="text-sm">{option.icon}</span>
                      <span>{option.label}</span>
                      {linkedTask && linkedTask[option.value + "At"] ? (
                        <span className="opacity-40 pl-[4px] font-normal">
                          {formatDate(linkedTask[option.value + "At"])}
                        </span>
                      ) : (
                        <span className="opacity-70 pl-[4px] font-normal">
                          {"未设置"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            {config.type === "task-timer" && (
              <>
                <div className="spacer"></div>
                <span className="settings-header">{t("v3/clock:settings.notifications", "倒计时结束提醒设置")}</span>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-[var(--cm-comp-bg-secondary)] rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🔔</span>
                      <span>铃声提醒</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={config.soundNotification || false}
                          onChange={(e) => handleSoundNotificationToggle(e.target.checked)}
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${
                          config.soundNotification ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                            config.soundNotification ? 'translate-x-5' : 'translate-x-0.5'
                          } mt-0.5`}></div>
                        </div>
                      </label>
                      {config.soundNotification && (
                        <>
                          <select
                            value={config.soundFile || 'vivora'}
                            onChange={(e) => handleSoundFileChange(e.target.value)}
                            className="min-w-[80px] px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="vivora">Vivora</option>
                            <option value="delay">Delay</option>
                            <option value="sms">SMS</option>
                          </select>
                          <button
                            className={`text-sm px-2 py-1 rounded transition duration-200 ${
                              isTestingSound
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                            onClick={isTestingSound ? stopTestAudio : handleTestSound}
                          >
                            {isTestingSound ? '停止' : '试听'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-[var(--cm-comp-bg-secondary)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">📳</span>
                        <span>震动提醒</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={config.vibrationNotification || false}
                          onChange={(e) => handleVibrationNotificationToggle(e.target.checked)}
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${
                          config.vibrationNotification ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                            config.vibrationNotification ? 'translate-x-5' : 'translate-x-0.5'
                          } mt-0.5`}></div>
                        </div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">更多设备适配中</span>
                      <button
                        onClick={handleTestVibration}
                        className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                      >
                        测试震动
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            <ClearConfigurableAreaButton onClear={handleClearArea} />
          </div>
        </ClockSettingsModal>
      )}

      {/* 自定义文字编辑器 */}
      {showTextEditor && (
        <ClockSettingsModal
          show={showTextEditor}
          setShow={handleTextEditorClose}
          header={"编辑「文字记录」小组件"}
        >
          <div className="inner">
            <span className="tips">
              {"以你喜欢的方式记录和展示独属于你的文本。"}
            </span>
            <div className="spacer"></div>
            <span className="settings-header">{"文本记录"}</span>
            <textarea
              ref={textAreaRef} // 新增
              className="w-full h-32 p-2"
              placeholder={t(
                "v3/clock:settings.customTextPlaceholder",
                "输入要显示的文字..."
              )}
              value={customTextDraft}
              onChange={(e) => setCustomTextDraft(e.target.value)}
            />
            <ClearConfigurableAreaButton onClear={handleClearArea} />
          </div>
        </ClockSettingsModal>
      )}

      {showTaskListEditor && (
        <ClockSettingsModal
          show={showTaskListEditor}
          setShow={setShowTaskListEditor}
          header={"编辑「待办事项」小组件"}
        >
          <div className="inner">
            <span className="tips">{"查看最近的待办事项。"}</span>

            <div className="spacer"></div>
            <span className="settings-header">{"显示范围"}</span>
            <div className="option-list">
              <button
                onClick={() => handleTimeRangeSelect("today")}
                className={`option-button ${
                  (config.taskTimeRange || "today-tomorrow") === "today" ? "active" : ""
                }`}
              >
                <span className="text-sm">📅</span>
                <span>只看今天</span>
              </button>
              <button
                onClick={() => handleTimeRangeSelect("today-tomorrow")}
                className={`option-button ${
                  (config.taskTimeRange || "today-tomorrow") === "today-tomorrow" ? "active" : ""
                }`}
              >
                <span className="text-sm">📆</span>
                <span>今天+明天</span>
              </button>
              <button
                onClick={() => handleTimeRangeSelect("yesterday-today-tomorrow")}
                className={`option-button ${
                  (config.taskTimeRange || "today-tomorrow") === "yesterday-today-tomorrow" ? "active" : ""
                }`}
              >
                <span className="text-sm">🗓️</span>
                <span>昨天+今天+明天</span>
              </button>
              <button
                onClick={() => handleTimeRangeSelect("all-incomplete")}
                className={`option-button ${
                  (config.taskTimeRange || "today-tomorrow") === "all-incomplete" ? "active" : ""
                }`}
              >
                <span className="text-sm">📋</span>
                <span>所有未完成</span>
              </button>
            </div>

            <div className="spacer"></div>
            <span className="settings-header">{"操作"}</span>
            <button
              className="action-button w-full hover:opacity-80 active:opacity-50 transition duration-200"
              onClick={() => navigate("/v3/taskv2")}
            >
              {"前往任务页面"}
            </button>
            <ClearConfigurableAreaButton onClear={handleClearArea} />
          </div>
        </ClockSettingsModal>
      )}
    </div>
  );
};

export default ConfigurableArea;
